export type AgentPageStatus = 'draft' | 'active' | 'disabled';

export type PreviousVersion = {
	version: number;
	source: string;
	compiledBundle: string | null;
	savedAt: string;
	savedBy: number | null;
};

export type AgentPage = {
	id: number;
	name: string;
	slug: string;
	status: AgentPageStatus;
	version: number;
	isInternalOnly: boolean;
	regDate: string;
	modDate: string;
	regBy: number | null;
	modBy: number | null;
	// Returned only by getOne (omitted from list payload for size)
	source?: string;
	previousVersions?: PreviousVersion[] | null;
};

export type SaveAgentPageInput = {
	id?: number;
	name?: string;
	slug?: string;
	source: string;
	isInternalOnly?: boolean;
};

class AgentPages {
	async list(): Promise<AgentPage[]> {
		const res = await fetch('/api/v2/agentPage/list');
		if (!res.ok) throw new Error(`Failed to list pages (${res.status})`);
		const body = await res.json();
		return body.data ?? [];
	}

	async getOne(id: number): Promise<AgentPage | null> {
		const res = await fetch(`/api/v2/agentPage/list?id=${encodeURIComponent(id)}`);
		if (!res.ok) throw new Error(`Failed to load page (${res.status})`);
		const body = await res.json();
		const rows: AgentPage[] = body.data ?? [];
		return rows[0] ?? null;
	}

	async save(input: SaveAgentPageInput): Promise<AgentPage> {
		const res = await fetch('/api/v2/agentPage/save', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(input)
		});
		const body = await res.json().catch(() => null);
		if (!res.ok) {
			const message = body?.error || `Failed to save page (${res.status})`;
			throw new Error(typeof message === 'string' ? message : 'Failed to save page');
		}
		return body.data;
	}

	async publish(id: number): Promise<AgentPage> {
		const res = await fetch(`/api/v2/agentPage/${encodeURIComponent(id)}/publish`, { method: 'POST' });
		const body = await res.json().catch(() => null);
		if (!res.ok) {
			const message = body?.error || `Failed to publish page (${res.status})`;
			throw new Error(typeof message === 'string' ? message : 'Failed to publish page');
		}
		return body.data;
	}
}

const resource = new AgentPages();
export default resource;
